import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import type { FeatureBlockContent } from '~/content/types';
import { Section } from '~/components/layout';
import { Grid, Icon, ResponsiveText, SmartImage } from '~/components/ui';
import { cn } from '~/lib/cn';
import { useLocalizedPath } from '~/lib/useLanguage';

/**
 * The image slot shows `content.image`, or — for blocks without a photo —
 * custom `media` (e.g. a coded illustration panel) that sizes itself like the
 * image does. Exactly one of the two must be provided.
 */
type FeatureBlockMedia =
  | { content: FeatureBlockContent }
  | { content: Omit<FeatureBlockContent, 'image'>; media: ReactNode };

/**
 * Two-column block: image on one side, titled icon-row list on the other
 * (Figma Receive / Transfer / Convert pattern).
 */
export function FeatureBlock(
  props: FeatureBlockMedia & {
    imagePosition?: 'left' | 'right';
    sectionClassName?: string;
    /** Optional page-level image sizing override. */
    imageClassName?: string;
    /** Optional card/element centred over the image (e.g. the Transfer selector). */
    imageOverlay?: ReactNode;
    /** Optional custom CTA rendered below the rows (e.g. the Download QR), replacing any text link. */
    ctaSlot?: ReactNode;
    /** Page-level headline type override (use `!` / breakpoint-scoped classes). */
    titleClassName?: string;
    /** Optional element under the heading rule (e.g. the traveler card-tier tags). */
    titleSlot?: ReactNode;
    /**
     * Pin the image while the rows scroll past. Off by default: the image is a
     * fixed 720px tall, so on blocks whose text column is shorter than that there
     * is nothing to scroll and sticky does nothing — and on short viewports a
     * 720px sticky element is taller than the space it has to stick in.
     */
    stickyImage?: boolean;
    /**
     * `landing`: the card landing pages' layout (tech Figma 1362:65854). The
     * image fills its half of the row instead of four grid columns, the heading
     * is H2 size, and rows use the tighter mobile spacing and 15px copy.
     */
    variant?: 'default' | 'landing';
  },
) {
  const {
    content,
    imagePosition = 'left',
    sectionClassName,
    imageClassName,
    imageOverlay,
    ctaSlot,
    titleClassName,
    titleSlot,
    stickyImage = false,
    variant = 'default',
  } = props;
  const landing = variant === 'landing';
  const localizedPathFor = useLocalizedPath();
  return (
    <Section className={sectionClassName}>
      {/* On the 9-col grid: image spans 4, text spans 4, with an empty column
          between them for breathing room. Full width + stacked on mobile. */}
      <Grid className={cn('items-center', landing ? 'gap-y-14' : 'gap-y-10')}>
        <div
          className={cn(
            'relative col-span-9 overflow-hidden lg:col-span-4 lg:row-start-1',
            imagePosition === 'right' ? 'lg:col-start-6' : 'lg:col-start-1',
            // Widen the 4-column area (A) to half the 9-column row: A·9/8 + 12.5px.
            // The right-hand image keeps the design's 20px gap before the centre line.
            landing &&
              (imagePosition === 'right'
                ? 'lg:-ms-[calc(12.5%+2.5px)]'
                : 'lg:-me-[calc(12.5%+12.5px)]'),
            // `self-start` is required — the Grid sets `items-center`, and a
            // stretched or centred grid item cannot stick.
            stickyImage && 'lg:sticky lg:top-24 lg:self-start',
          )}>
          {'media' in props ? (
            props.media
          ) : (
            <SmartImage
              image={props.content.image}
              className={cn('aspect-square w-full lg:aspect-auto lg:h-[720px]', imageClassName)}
            />
          )}
          {imageOverlay && (
            <div className="pointer-events-none absolute inset-0 grid place-items-center">
              {imageOverlay}
            </div>
          )}
        </div>

        <div
          className={cn(
            'col-span-9 lg:col-span-4 lg:row-start-1',
            imagePosition === 'right' ? 'lg:col-start-1' : 'lg:col-start-6',
            landing && 'lg:self-start lg:pt-16',
            // The design insets the left-hand copy 40px and runs it to the image's gap.
            landing && imagePosition === 'right' && 'lg:-me-9 lg:ps-10',
          )}>
          <div className={cn('border-b border-black-6', landing ? 'pb-8' : 'pb-6')}>
            <h2
              className={cn(
                'font-semibold whitespace-pre-line',
                landing
                  ? 'text-h3 leading-[1.16] tracking-[-0.022em] md:text-h2'
                  : 'text-h4 tracking-[-0.02em] md:text-h3',
                titleClassName,
              )}>
              {content.title}
            </h2>
          </div>
          {titleSlot && <div className="mt-8">{titleSlot}</div>}
          {content.description && (
            <p className="mt-8 text-body text-grey-900">{content.description}</p>
          )}
          <ul className={cn(content.description ? 'mt-8' : landing ? 'mt-6' : 'mt-4')}>
            {content.rows.map((row, i) => (
              <li key={row.title} className="flex gap-5">
                {/* Icon chip nudged down to line up with the padded text block. */}
                <span
                  className={cn(
                    'flex size-12 shrink-0 items-center justify-center rounded-full text-ink',
                    landing ? 'mt-6 bg-black-6 md:mt-8' : 'mt-8 bg-grey-50',
                  )}>
                  {row.logo ? (
                    <img
                      src={row.logo}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                      decoding="async"
                      className="size-6 object-contain"
                    />
                  ) : (
                    <Icon
                      name={row.icon}
                      className={row.icon.startsWith('flag-') ? 'size-8' : 'size-6'}
                    />
                  )}
                </span>
                {/* Divider lives on the text column so it starts aligned with the text, not under the icon. */}
                <div className={cn('flex-1', i > 0 && 'border-t border-black-6')}>
                  {/* 32px top/bottom padding → 32px from content to each separator; 20px between title and paragraph. */}
                  {/* Landing rows sit 6px lower than their icon chip, as in the design. */}
                  <div
                    className={cn(
                      'flex flex-col',
                      landing ? 'gap-4 py-[30px] md:gap-5 md:py-[38px]' : 'gap-5 py-8',
                    )}>
                    <h3 className="text-body font-semibold">{row.title}</h3>
                    <p
                      className={cn(
                        'text-body text-grey-900',
                        landing && 'max-md:text-[15px] max-md:tracking-[0.27px]',
                      )}>
                      <ResponsiveText text={row.description} mobileText={row.mobileDescription} />
                    </p>
                    {row.bullets && (
                      <ul className="list-disc space-y-3 ps-5 text-body text-grey-900 marker:text-red-500">
                        {row.bullets.map((bullet) => (
                          <li key={bullet}>{bullet}</li>
                        ))}
                      </ul>
                    )}
                    {row.detail && (
                      <p className="text-body-m font-semibold text-ink">{row.detail}</p>
                    )}
                    {row.link && (
                      <Link
                        to={localizedPathFor(row.link.href)}
                        className="text-body-m font-medium text-red-500 hover:text-red-700">
                        {row.link.label}
                      </Link>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {ctaSlot && <div className="mt-8">{ctaSlot}</div>}
        </div>
      </Grid>
    </Section>
  );
}
